import type { AvailabilityCard, WebRTCConnectionState } from '@questvisionstream/client';

/**
 * The warm-up card's state machine as a pure function - the web return of
 * Unity's `StartupFlowController.Render()` (itself a port of the original web
 * intro), extended with the LiveKit path's D24 waiting window.
 *
 * Tier order, first match wins:
 *   0. started            -> card hidden (the session owns the screen now)
 *   1. starting           -> "Starting…"
 *   2. XR support         -> "Checking XR…" / "XR unavailable"
 *   3. camera pre-flight  -> "Checking camera…" / "Camera unavailable"
 *   4. D24 waiting window -> "Join" (enabled only when the cell is free)
 *   5. room connection    -> "Connecting…" / "Enter" (enabled) / failure note
 *
 * Enter only enables once the room is fully connected: by the time the user
 * can press it, frames flow instantly. The press is the consent moment - the
 * FIRST time camera pixels may leave the device (`beginStreaming`).
 */

export type WarmupCameraState = 'checking' | 'ready' | 'error';

export interface WarmupState {
  /** The streaming session is live - the card is done. */
  readonly started: boolean;
  /** Enter/Join was pressed; the launch is in flight. */
  readonly starting: boolean;
  /** `navigator.xr.isSessionSupported` has resolved. */
  readonly xrChecked: boolean;
  readonly xrSupported: boolean;
  readonly cameraState: WarmupCameraState;
  /** The camera pre-flight's failure sentence, when cameraState is 'error'. */
  readonly cameraError: string | null;
  /** The room transport's connection state. */
  readonly roomState: WebRTCConnectionState;
  /** The D24 waiting window is open (a 409 in_use refusal). */
  readonly waiting: boolean;
  /** The availability card while waiting or blocked; null otherwise. */
  readonly availability: AvailabilityCard | null;
  /** Why discovery failed, when it failed with no waiting window. */
  readonly discoveryFailure: string | null;
}

export interface WarmupCard {
  /** Card visible at all? False once the session is live. */
  readonly visible: boolean;
  readonly buttonLabel: string;
  readonly buttonEnabled: boolean;
  readonly note: string;
  readonly noteIsError: boolean;
  /** Show the manual config-endpoint row (server unreachable - RA-C10 fallback). */
  readonly showAddressPrompt: boolean;
  /** What a press means right now - the click handler dispatches on this. */
  readonly action: 'none' | 'enter' | 'join';
}

const HIDDEN: WarmupCard = {
  visible: false,
  buttonLabel: '',
  buttonEnabled: false,
  note: '',
  noteIsError: false,
  showAddressPrompt: false,
  action: 'none',
};

function card(partial: Partial<WarmupCard> & Pick<WarmupCard, 'buttonLabel' | 'buttonEnabled'>): WarmupCard {
  return {
    visible: true,
    note: '',
    noteIsError: false,
    showAddressPrompt: false,
    action: 'none',
    ...partial,
  };
}

export function renderWarmupCard(state: WarmupState): WarmupCard {
  if (state.started) return HIDDEN;

  if (state.starting) {
    return card({ buttonLabel: 'Starting…', buttonEnabled: false });
  }

  // Tier 2 - the platform gate: immersive AR itself.
  if (!state.xrChecked) {
    return card({ buttonLabel: 'Checking XR…', buttonEnabled: false });
  }
  if (!state.xrSupported) {
    return card({
      buttonLabel: 'XR unavailable',
      buttonEnabled: false,
      note: 'Immersive AR is not supported here - open this page on a Meta Quest.',
      noteIsError: true,
    });
  }

  // Tier 3 - the passthrough camera pre-flight. The camera comes up BEFORE
  // Enter (Unity parity); frames still leave the device only after Enter.
  if (state.cameraState === 'error') {
    return card({
      buttonLabel: 'Camera unavailable',
      buttonEnabled: false,
      note: `Passthrough camera isn't available - ${state.cameraError ?? 'check headset permissions.'}`,
      noteIsError: true,
    });
  }
  if (state.cameraState !== 'ready') {
    return card({
      buttonLabel: 'Checking camera…',
      buttonEnabled: false,
      note: 'Waiting for the passthrough camera…',
    });
  }

  // Tier 4 - the D24 waiting window: the cell is busy. Join is a hint, never
  // a reservation, and it enables only when the read-only status poll says
  // the cell is free.
  if (state.waiting) {
    const availability = state.availability;
    return card({
      buttonLabel: 'Join',
      buttonEnabled: availability?.joinEnabled ?? false,
      note: availability?.detail ?? 'This training server is already in use.',
      noteIsError: availability ? availability.severity === 'error' : false,
      action: availability?.joinEnabled ? 'join' : 'none',
    });
  }

  // Tier 5 - connection-driven, verbatim from the original intro. The session
  // negotiates during this screen, so Enter only lights once it is warm.
  if (state.roomState === 'connected') {
    return card({ buttonLabel: 'Enter', buttonEnabled: true, action: 'enter' });
  }

  if (state.roomState === 'failed' || state.roomState === 'closed') {
    // Blocked refusals carry the actionable sentence (the availability card);
    // plain transport failures fall back to the classic hint.
    const detail = state.availability?.detail ?? null;
    return card({
      buttonLabel: 'Enter',
      buttonEnabled: false,
      note:
        detail ??
        state.discoveryFailure ??
        'Unable to connect to the assistance service - is the deployment configured and reachable?',
      noteIsError: true,
      showAddressPrompt: true,
    });
  }

  return card({
    buttonLabel: 'Connecting…',
    buttonEnabled: false,
    note: 'Connecting to the assistance service…',
  });
}
